import { SUPABASE_FUNCTIONS_URL, SUPABASE_ANON_KEY } from "@/integrations/supabase/client";
import { powtoonBE } from "@/lib/powtoon-be/client";
import { liveavatar } from "@/lib/liveavatar/client";
import { buildContextPrompt } from "@/lib/prompts";
import type {
  CreateScenarioInput,
  CtaType,
  Scenario,
  ScenarioSourceItem,
} from "@/lib/powtoon-be/types";

export type LegacyDocument = {
  id: string;
  file_name: string;
  storage_path?: string;
  public_url: string;
};

export type LegacySession = {
  id: string;
  title?: string | null;
  description?: string | null;
  owner_email?: string | null;
  avatar_id?: string | null;
  avatar_name?: string | null;
  avatar_preview_url?: string | null;
  voice_id?: string | null;
  language?: string | null;
  knowledge_base_id?: string | null;
  mode?: string | null;
  voiceOnly?: boolean | null;
  created_at?: string | null;
  documents?: LegacyDocument[];
  // New fields surfaced by the legacy export endpoint:
  opening_sentence?: string | null;
  kb_content?: string | null;
  cta_type?: string | null;
  cta_booking_url?: string | null;
  redirect_url?: string | null;
  auto_start?: boolean | null;
};

const FN_BASE = `${SUPABASE_FUNCTIONS_URL}/import-legacy-session`;

function authHeaders() {
  return {
    apikey: SUPABASE_ANON_KEY,
    authorization: `Bearer ${SUPABASE_ANON_KEY}`,
  };
}

export async function listLegacySessions(): Promise<LegacySession[]> {
  const res = await fetch(`${FN_BASE}?action=list`, { headers: authHeaders() });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`list ${res.status}: ${body.slice(0, 300)}`);
  }
  const data = (await res.json()) as { sessions: LegacySession[] };
  return data.sessions ?? [];
}

async function downloadLegacyDoc(publicUrl: string): Promise<Blob> {
  // Try direct first (Supabase public storage usually allows CORS); fall back
  // to our proxy on any failure (CORS, network, opaque response).
  try {
    const r = await fetch(publicUrl);
    if (r.ok) return await r.blob();
  } catch {
    /* fall through to proxy */
  }
  const proxied = await fetch(
    `${FN_BASE}?action=download&url=${encodeURIComponent(publicUrl)}`,
    { headers: authHeaders() },
  );
  if (!proxied.ok) {
    throw new Error(`download failed (${proxied.status})`);
  }
  return await proxied.blob();
}

export type ImportProgress =
  | { phase: "pending" }
  | { phase: "creating" }
  | { phase: "context" }
  | { phase: "thumbnail" }
  | { phase: "uploading"; done: number; total: number }
  | { phase: "done"; scenarioId: string; docErrors: string[] }
  | { phase: "error"; message: string };

function buildScenarioInput(s: LegacySession): CreateScenarioInput {
  const modeNote = s.mode ? ` (original mode: ${s.mode})` : "";
  const description = `[Imported from legacy studio]${modeNote} ${s.description ?? ""}`.trim();
  // Only "sales" maps cleanly; anything else collapses to "none".
  const ctaType: CtaType = s.cta_type === "sales" ? "sales" : "none";
  return {
    name: s.title?.trim() || "Untitled (imported)",
    description,
    thumb_url: s.avatar_preview_url ?? undefined,
    content: {
      avatar_id: s.avatar_id ?? undefined,
      voice_id: s.voice_id ?? undefined,
      language: s.language ?? undefined,
      // No `context_id` / `liveavatar_context_id` here: importLegacySession
      // creates a fresh LiveAvatar context per imported scenario so edits
      // never mutate the legacy studio's shared context.
      status: "draft",
      publish_mode: "hidden",
      cta_type: ctaType,
      context_block: {
        interaction_mode: "conversational",
        inputs: [],
        persona: "",
        knowledge: s.kb_content ?? "",
        instructions: "",
        opening_text: s.opening_sentence ?? "",
        generated_prompt: "",
      },
    },
  };
}

/**
 * Import a single legacy session as a new scenario. Reuses the existing
 * LiveAvatar context (no calls to context-generation APIs). Documents are
 * downloaded from the legacy public URL and re-uploaded as Powtoon
 * attachments; per-doc failures are collected, not fatal.
 */
export async function importLegacySession(
  session: LegacySession,
  onProgress?: (p: ImportProgress) => void,
): Promise<{ scenario: Scenario; docErrors: string[] }> {
  onProgress?.({ phase: "creating" });
  let scenario = await powtoonBE.scenarios.create(buildScenarioInput(session));

  const docErrors: string[] = [];

  // Create a brand-new LiveAvatar context for this scenario (do not reuse
  // the legacy one). Seed it with the same knowledge + opening sentence so
  // the imported scenario behaves like the original.
  onProgress?.({ phase: "context" });
  try {
    const knowledge = session.kb_content ?? "";
    const openingText = session.opening_sentence ?? "";
    const prompt = buildContextPrompt({
      mode: "conversational",
      persona: "",
      knowledge,
      instructions: "",
    });
    const { context_id: newContextId } = await liveavatar.createContext({
      context_name: scenario.name ?? "Imported scenario",
      context_prompt: prompt,
      opening_text: openingText,
    });
    const currentBlock = scenario.content.context_block;
    scenario = await powtoonBE.scenarios.update(scenario.id, {
      content: {
        ...scenario.content,
        context_id: newContextId,
        context_block: {
          ...(currentBlock ?? {
            interaction_mode: "conversational",
            persona: "",
            knowledge,
            instructions: "",
            opening_text: openingText,
            inputs: [],
            generated_prompt: "",
          }),
          liveavatar_context_id: newContextId,
          generated_prompt: prompt,
        },
      },
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    docErrors.push(`context: ${msg}`);
  }

  // Upload the legacy avatar preview as a real thumbnail file (the app's
  // scenario cards render the uploaded thumb, not `thumb_url`).
  if (session.avatar_preview_url) {
    onProgress?.({ phase: "thumbnail" });
    try {
      scenario = await powtoonBE.scenarios.setThumbnailFromUrl(
        scenario.id,
        session.avatar_preview_url,
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      docErrors.push(`thumbnail: ${msg}`);
    }
  }

  const docs = session.documents ?? [];
  const uploadedInputs: ScenarioSourceItem[] = [];
  if (docs.length > 0) {
    onProgress?.({ phase: "uploading", done: 0, total: docs.length });
    for (let i = 0; i < docs.length; i++) {
      const doc = docs[i];
      try {
        const blob = await downloadLegacyDoc(doc.public_url);
        const mime = blob.type || "application/octet-stream";
        const file = new File([blob], doc.file_name, { type: mime });
        const attachment = await powtoonBE.attachments.upload(
          scenario.id,
          file,
          doc.file_name,
        );
        uploadedInputs.push({
          kind: "upload",
          object_id: attachment.id,
          file_name: doc.file_name,
          mime,
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        docErrors.push(`${doc.file_name}: ${msg}`);
      }
      onProgress?.({ phase: "uploading", done: i + 1, total: docs.length });
    }
  }

  // Register the uploaded attachments on the scenario so the editor lists
  // them under context_block.inputs[]. Skip if none uploaded successfully.
  if (uploadedInputs.length > 0) {
    try {
      const currentBlock = scenario.content.context_block;
      scenario = await powtoonBE.scenarios.update(scenario.id, {
        content: {
          ...scenario.content,
          context_block: {
            ...(currentBlock ?? {
              interaction_mode: "conversational",
              persona: "",
              knowledge: "",
              instructions: "",
              opening_text: "",
              generated_prompt: "",
            }),
            inputs: uploadedInputs,
          },
        },
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      docErrors.push(`register inputs: ${msg}`);
    }
  }

  onProgress?.({ phase: "done", scenarioId: scenario.id, docErrors });
  return { scenario, docErrors };
}